import { Controller } from 'stimulus'
import CheckoutController from './checkout_controller'

export type AddressForm = {
  phone: string
  address1: string
  address2: string | null
  city: string
  state: string
  zip: string
  sms_opt_in: boolean
}

export default class FormAddressController extends Controller {
  // Targets
  declare phoneTarget: HTMLInputElement
  declare address1Target: HTMLInputElement
  declare address2Target: HTMLInputElement
  declare cityTarget: HTMLInputElement
  declare stateTarget: HTMLInputElement
  declare zipTarget: HTMLInputElement
  declare smsOptInTarget: HTMLInputElement
  declare smsOptInLabelTarget: HTMLElement
  declare checkIconTarget: HTMLElement // Check icon within smsOptIn checkbox
  declare loadingTargets: HTMLElement[]
  declare notLoadingTargets: HTMLElement[]
  declare errorTargets: HTMLElement[]

  static targets = [
    'phone',
    'address1',
    'address2',
    'city',
    'state',
    'zip',
    'smsOptIn',
    'smsOptInLabel',
    'checkIcon',
    'loading',
    'notLoading',
    'error',
  ]

  declare checkoutOutlet: CheckoutController
  static outlets = ['checkout']
  private loading = false

  connect() {
    this.phoneTarget.focus()
    this.setInitialValues()
    this.checkoutOutlet.onPage2()

    this.smsOptInLabelTarget.addEventListener(
      'click',
      this.toggleCheckbox.bind(this),
    )
  }

  setInitialValues() {
    if (!this.checkoutOutlet.addressForm) return
    const { phone, address1, address2, city, state, zip, sms_opt_in } =
      this.checkoutOutlet.addressForm

    this.phoneTarget.value = phone
    this.address1Target.value = address1
    this.address2Target.value = address2 || ''
    this.cityTarget.value = city
    this.stateTarget.value = state
    this.zipTarget.value = zip
    this.smsOptInTarget.checked = sms_opt_in || false
    this.toggleCheckIcon()
  }

  setLoading() {
    this.loading = true
    this.loadingTargets.forEach((target) => (target.hidden = false))
    this.notLoadingTargets.forEach((target) => (target.hidden = true))
  }

  setNotLoading() {
    this.loading = false
    this.loadingTargets.forEach((target) => (target.hidden = true))
    this.notLoadingTargets.forEach((target) => (target.hidden = false))
  }

  showErrors() {
    this.errorTargets.forEach((target) => (target.hidden = false))
  }

  hideErrors() {
    this.errorTargets.forEach((target) => (target.hidden = true))
  }

  toggleCheckbox() {
    this.smsOptInTarget.checked = !this.smsOptInTarget.checked
    this.smsOptInTarget.dispatchEvent(new Event('change')) // Trigger change event as if user clicked checkbox
    this.toggleCheckIcon()
  }

  toggleCheckIcon() {
    if (this.smsOptInTarget.checked) {
      this.checkIconTarget.classList.remove('hidden')
      this.checkIconTarget.classList.add('block')
    } else {
      this.checkIconTarget.classList.add('hidden')
      this.checkIconTarget.classList.remove('block')
    }
  }

  submit(event: SubmitEvent) {
    if (!event.target) throw new Error('Missing event target')
    if (this.loading) return

    const formData = new FormData(event.target as HTMLFormElement)
    const addressForm: AddressForm = {
      phone: formData.get('phone') as string,
      address1: formData.get('address1') as string,
      address2: formData.get('address2') as string | null,
      city: formData.get('city') as string,
      state: formData.get('state') as string,
      zip: formData.get('zip') as string,
      sms_opt_in: formData.get('sms_opt_in') === 'true',
    }

    this.checkoutOutlet.setAddressForm(addressForm)
  }

  async submitPartner(event: TurboSubmitEvent) {
    if (!event.detail.success) return

    event.preventDefault()
    this.setLoading()

    try {
      await this.checkoutOutlet.submitPartner()
    } catch (error) {
      this.setNotLoading()
      this.showErrors()
      throw error
    }
  }
}

type TurboSubmitEvent = SubmitEvent & {
  detail: {
    success: boolean
  }
}
